import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import {
  Page,
  Panel,
  Tabs,
  Button,
  Badge,
  SecretField,
  IntegrationCard,
  AuditTimeline,
  Toast,
  Skeleton,
  KeyIcon,
  PlusIcon,
} from '../../admin-ui';

export default function AdminSettings() {
  const [activeTab, setActiveTab] = useState('negocio');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [data, setData] = useState({
    settings: null,
    project: null,
    accounts: [],
    integrations: [],
    audit: [],
    syncJobs: [],
  });

  const load = async () => {
    setLoading(true);
    try {
      const [settings, project, accounts, integrations, audit, syncJobs] = await Promise.all([
        api.get('/admin/settings').catch(() => null),
        api.get('/admin/project').catch(() => null),
        api.get('/admin/accounts').catch(() => []),
        api.get('/admin/integrations').catch(() => []),
        api.get('/admin/audit?limit=20').catch(() => []),
        api.get('/admin/portal-sync/jobs').catch(() => []),
      ]);
      setData({ settings, project, accounts, integrations, audit, syncJobs });
    } catch (e) {
      setToast({ message: 'Error cargando configuración: ' + e.message, variant: 'danger' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const showToast = (message, variant = 'success') => {
    setToast({ message, variant });
    setTimeout(() => setToast(null), 4000);
  };

  const saveSettings = async (patch) => {
    setSaving(true);
    try {
      await api.patch('/admin/settings', patch);
      showToast('Configuración guardada');
      await load();
    } catch (e) {
      showToast(e.message || 'Error guardando', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const saveProject = async (patch) => {
    setSaving(true);
    try {
      await api.patch('/admin/project', patch);
      showToast('Proyecto actualizado');
      await load();
    } catch (e) {
      showToast(e.message || 'Error guardando', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const saveAccount = async (id, patch) => {
    setSaving(true);
    try {
      await api.patch(`/admin/accounts/${id}`, patch);
      showToast('Cuenta actualizada');
      await load();
    } catch (e) {
      showToast(e.message || 'Error guardando', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const createAccount = async (payload) => {
    setSaving(true);
    try {
      await api.post('/admin/accounts', payload);
      showToast('Cuenta corporativa creada');
      await load();
    } catch (e) {
      showToast(e.message || 'Error creando cuenta', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const rotateAccountSecret = async (id, plainSecret) => {
    setSaving(true);
    try {
      await api.post(`/admin/credentials/managedaccount/${id}/secret`, { plainSecret });
      showToast('Contraseña actualizada');
      await load();
    } catch (e) {
      showToast(e.message || 'Error cambiando contraseña', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const saveIntegration = async (id, patch) => {
    setSaving(true);
    try {
      await api.patch(`/admin/integrations/${id}`, patch);
      showToast('Integración actualizada');
      await load();
    } catch (e) {
      showToast(e.message || 'Error guardando', 'danger');
    } finally {
      setSaving(false);
    }
  };

  const revealAccountSecret = async (id) => {
    const res = await api.post(`/admin/credentials/managedaccount/${id}/reveal`);
    return res.secret || res.value || '';
  };

  const revealIntegrationSecret = async (id) => {
    const res = await api.post(`/admin/credentials/integration/${id}/reveal`);
    return res.secret || res.value || '';
  };

  const tabs = [
    { key: 'negocio', label: 'Negocio' },
    { key: 'fiscal', label: 'Datos fiscales' },
    { key: 'proyecto', label: 'Proyecto' },
    { key: 'cuentas', label: 'Info corporativa' },
    { key: 'integraciones', label: 'Integraciones' },
    { key: 'sync', label: 'Sync' },
    { key: 'auditoria', label: 'Auditoría' },
  ];

  return (
    <Page
      title="Configuración"
      description="Gestión del negocio, integraciones y sincronización"
      actions={
        <Button variant="secondary" onClick={load} disabled={loading}>
          {loading ? 'Actualizando...' : 'Actualizar'}
        </Button>
      }
    >
      <Toast {...toast} onClose={() => setToast(null)} />
      <Tabs tabs={tabs} active={activeTab} onChange={setActiveTab} />

      {activeTab === 'negocio' && (
        <NegocioPanel
          settings={data.settings}
          loading={loading}
          saving={saving}
          onSave={saveSettings}
        />
      )}
      {activeTab === 'proyecto' && (
        <ProyectoPanel
          project={data.project}
          loading={loading}
          saving={saving}
          onSave={saveProject}
        />
      )}
      {activeTab === 'fiscal' && (
        <FiscalPanel
          settings={data.settings}
          loading={loading}
          saving={saving}
          onSave={saveSettings}
        />
      )}
      {activeTab === 'cuentas' && (
        <CuentasPanel
          accounts={data.accounts}
          loading={loading}
          saving={saving}
          onSave={saveAccount}
          onCreate={createAccount}
          onRotateSecret={rotateAccountSecret}
          onReveal={revealAccountSecret}
        />
      )}
      {activeTab === 'integraciones' && (
        <IntegracionesPanel
          integrations={data.integrations}
          loading={loading}
          saving={saving}
          onSave={saveIntegration}
          onReveal={revealIntegrationSecret}
        />
      )}
      {activeTab === 'sync' && (
        <SyncPanel jobs={data.syncJobs} loading={loading} />
      )}
      {activeTab === 'auditoria' && (
        <Panel title="Auditoría">
          {loading ? (
            <Skeleton width="100%" height="8rem" />
          ) : (
            <AuditTimeline
              items={data.audit.map((a) => ({
                title: `${a.action} — ${a.entityType}`,
                time: new Date(a.createdAt).toLocaleString('es-ES'),
                description: a.actorEmail || a.ip || '',
              }))}
            />
          )}
        </Panel>
      )}
    </Page>
  );
}

/* ── Sub-paneles ── */

function NegocioPanel({ settings, loading, saving, onSave }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (settings?.business) {
      setForm({ ...settings.business });
    }
  }, [settings]);

  if (loading && !settings) return <Skeleton width="100%" height="12rem" />;
  if (!settings?.business) return <p style={{ color: 'var(--text-secondary)' }}>Sin datos de negocio.</p>;

  const business = settings.business;
  const fields = [
    { key: 'tradeName', label: 'Nombre comercial' },
    { key: 'legalName', label: 'Razón social' },
    { key: 'nif', label: 'NIF/CIF' },
    { key: 'address', label: 'Dirección' },
    { key: 'city', label: 'Ciudad' },
    { key: 'province', label: 'Provincia' },
    { key: 'postalCode', label: 'CP' },
    { key: 'phone', label: 'Teléfono' },
    { key: 'whatsapp', label: 'WhatsApp' },
    { key: 'publicEmail', label: 'Email público' },
    { key: 'bookingEmail', label: 'Email reservas' },
    { key: 'supportEmail', label: 'Email soporte' },
  ];

  return (
    <Panel title="Datos del negocio">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '0.75rem' }}>
        {fields.map((f) => (
          <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
            <span style={{ color: 'var(--text-secondary)' }}>{f.label}</span>
            <input
              value={form[f.key] || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
            />
          </label>
        ))}
      </div>
      <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
        <Button onClick={() => onSave({ business: form })} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>
    </Panel>
  );
}

function FiscalPanel({ settings, loading, saving, onSave }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!settings) return;
    const business = settings.business || {};
    const issuer = settings.issuer || {};
    setForm({
      issuer: {
        name: issuer.name || business.legalName || business.tradeName || '',
        nif: issuer.nif || business.nif || '',
        address: issuer.address || business.address || '',
        city: issuer.city || business.city || '',
        postalCode: issuer.postalCode || business.postalCode || '',
        phone: issuer.phone || business.phone || '',
        email: issuer.email || business.publicEmail || '',
      },
      defaultTaxRate: settings.defaultTaxRate ?? 10,
      taxSimplifiedThreshold: settings.taxSimplifiedThreshold ?? 400,
      invoiceCounters: settings.invoiceCounters || {},
    });
  }, [settings]);

  if (loading && !settings) return <Skeleton width="100%" height="12rem" />;

  const setIssuer = (key, value) => setForm((prev) => ({
    ...prev,
    issuer: { ...(prev.issuer || {}), [key]: value }
  }));

  return (
    <div style={{ display: 'grid', gap: '1rem' }}>
      <Panel title="Datos fiscales y facturación">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
          {[
            ['name', 'Razón social'],
            ['nif', 'NIF/CIF'],
            ['address', 'Dirección'],
            ['city', 'Ciudad'],
            ['postalCode', 'CP'],
            ['phone', 'Teléfono'],
            ['email', 'Email']
          ].map(([key, label]) => (
            <label key={key} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
              <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
              <input value={form.issuer?.[key] || ''} onChange={(e) => setIssuer(key, e.target.value)} />
            </label>
          ))}
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
            <span style={{ color: 'var(--text-secondary)' }}>IVA por defecto (%)</span>
            <input type="number" min="0" max="21" value={form.defaultTaxRate ?? 10} onChange={(e) => setForm((prev) => ({ ...prev, defaultTaxRate: Number(e.target.value) }))} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
            <span style={{ color: 'var(--text-secondary)' }}>Límite ticket simplificado</span>
            <input type="number" min="0" value={form.taxSimplifiedThreshold ?? 400} onChange={(e) => setForm((prev) => ({ ...prev, taxSimplifiedThreshold: Number(e.target.value) }))} />
          </label>
        </div>
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
          <Button onClick={() => onSave({ issuer: form.issuer, defaultTaxRate: form.defaultTaxRate, taxSimplifiedThreshold: form.taxSimplifiedThreshold })} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar datos fiscales'}
          </Button>
        </div>
      </Panel>
      <Panel title="Numeración">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '.75rem' }}>
          {['T', 'F'].map((serie) => {
            const counter = form.invoiceCounters?.[serie] || {};
            return (
              <div key={serie} style={{ border: '1px solid var(--a-border-md)', borderRadius: 10, padding: '.8rem' }}>
                <strong>Serie {serie}</strong>
                <p style={{ margin: '.35rem 0 0', color: 'var(--a-muted)' }}>Año {counter.year || new Date().getFullYear()}</p>
                <p style={{ margin: '.15rem 0 0', color: 'var(--a-muted)' }}>Último número {counter.lastNumber || 0}</p>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}

function ProyectoPanel({ project, loading, saving, onSave }) {
  const [form, setForm] = useState({});

  useEffect(() => {
    if (project?.project) {
      setForm({ ...project.project });
    }
  }, [project]);

  if (loading && !project) return <Skeleton width="100%" height="8rem" />;
  if (!project?.project) return <p style={{ color: 'var(--text-secondary)' }}>Sin datos de proyecto.</p>;

  return (
    <Panel title="Datos del proyecto">
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem' }}>
        {[
          { key: 'name', label: 'Nombre del proyecto' },
          { key: 'clientName', label: 'Cliente' },
          { key: 'clientProjectId', label: 'ID cliente' },
          { key: 'provisionalUrl', label: 'URL provisional' },
          { key: 'productionUrl', label: 'URL producción' },
          { key: 'adminUrl', label: 'URL admin' },
        ].map((f) => (
          <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
            <span style={{ color: 'var(--text-secondary)' }}>{f.label}</span>
            <input
              value={form[f.key] || ''}
              onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
            />
          </label>
        ))}
      </div>
      <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
        <Button onClick={() => onSave({ project: form })} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>
    </Panel>
  );
}

const accountTypeLabels = {
  email: 'Correo',
  admin_user: 'Admin',
  domain: 'Dominio',
  hosting: 'Hosting',
  social: 'Social',
  google: 'Google',
  sumup: 'SumUp',
  other: 'Otra',
};

const accountStatusLabels = {
  planned: 'Planificada',
  created: 'Creada',
  active: 'Activa',
  suspended: 'Suspendida',
  pending_migration: 'Pendiente migración',
};

function formatDate(value) {
  if (!value) return '';
  return new Date(value).toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function CuentasPanel({ accounts, loading, saving, onSave, onCreate, onRotateSecret, onReveal }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const [newAccount, setNewAccount] = useState({
    type: 'email',
    label: '',
    provider: 'Zimbra / mail.weltas.es',
    username: '',
    email: '',
    loginUrl: 'https://mail.weltas.es',
    status: 'active',
    owner: 'client',
    environment: 'production',
    purpose: '',
    notes: '',
    credential: { secretKind: 'password', plainSecret: '' },
  });
  const [rotatingId, setRotatingId] = useState(null);
  const [passwordForm, setPasswordForm] = useState('');

  const accountsByType = accounts.reduce((acc, item) => {
    const key = item.type || 'other';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  const resetNewAccount = () => {
    setNewAccount({
      type: 'email',
      label: '',
      provider: 'Zimbra / mail.weltas.es',
      username: '',
      email: '',
      loginUrl: 'https://mail.weltas.es',
      status: 'active',
      owner: 'client',
      environment: 'production',
      purpose: '',
      notes: '',
      credential: { secretKind: 'password', plainSecret: '' },
    });
  };

  const startEdit = (acc) => {
    setEditing(acc._id);
    setForm({
      label: acc.label || '',
      type: acc.type || 'other',
      provider: acc.provider || '',
      username: acc.username || '',
      email: acc.email || '',
      loginUrl: acc.loginUrl || '',
      status: acc.status || 'planned',
      owner: acc.owner || 'client',
      environment: acc.environment || 'production',
      purpose: acc.purpose || '',
      notes: acc.notes || '',
    });
  };

  const submitNewAccount = async () => {
    const username = newAccount.username || newAccount.email;
    await onCreate({
      ...newAccount,
      username,
      email: newAccount.email || username,
      credential: newAccount.credential?.plainSecret
        ? newAccount.credential
        : { secretKind: 'password' },
    });
    resetNewAccount();
    setShowCreate(false);
  };

  const submitRotate = async (id) => {
    if (!passwordForm.trim()) return;
    await onRotateSecret(id, passwordForm.trim());
    setPasswordForm('');
    setRotatingId(null);
  };

  if (loading && !accounts.length) return <Skeleton width="100%" height="8rem" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <Panel
        title="Información corporativa"
        action={
          <Button
            variant="secondary"
            iconLeft={<PlusIcon size={14} />}
            onClick={() => setShowCreate((v) => !v)}
          >
            {showCreate ? 'Cerrar alta' : 'Añadir cuenta'}
          </Button>
        }
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(220px, .6fr)', gap: '1rem', alignItems: 'start' }}>
          <div>
            <p style={{ margin: 0, color: 'var(--text-secondary)', lineHeight: 1.55 }}>
              Cuentas corporativas de NEON: correos, accesos administrativos, dominios y servicios externos.
              Las contraseñas se guardan cifradas y solo se muestran bajo acción explícita del administrador.
            </p>
            <div style={{ marginTop: '.75rem', display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
              <Badge variant="success">{accounts.length} cuentas</Badge>
              <Badge variant="secret">{accounts.filter((a) => a.credential?.hasSecret).length} con contraseña</Badge>
              {Object.entries(accountsByType).map(([type, count]) => (
                <Badge key={type} variant="neutral">{accountTypeLabels[type] || type}: {count}</Badge>
              ))}
            </div>
          </div>
          <div style={{ border: '1px solid var(--a-border-md)', borderRadius: 8, padding: '.85rem', background: 'rgba(14, 165, 233, .08)' }}>
            <strong style={{ display: 'block', marginBottom: '.35rem' }}>Ayuda correo en móvil/PC</strong>
            <p style={{ margin: '0 0 .7rem', color: 'var(--text-secondary)', fontSize: '.85rem', lineHeight: 1.45 }}>
              Manual para configurar el correo del cliente en su aplicación.
            </p>
            <Button
              variant="secondary"
              onClick={() => window.open('https://helpmail.weltascorp.com', '_blank', 'noopener,noreferrer')}
            >
              Abrir ayuda
            </Button>
          </div>
        </div>
      </Panel>

      {showCreate && (
        <Panel title="Nueva cuenta corporativa">
          <AccountForm form={newAccount} onChange={setNewAccount} includeSecret />
          <div style={{ marginTop: '1rem', display: 'flex', gap: '.5rem', flexWrap: 'wrap' }}>
            <Button onClick={submitNewAccount} disabled={saving || !(newAccount.email || newAccount.username)}>
              {saving ? 'Creando...' : 'Crear cuenta'}
            </Button>
            <Button variant="secondary" onClick={() => { resetNewAccount(); setShowCreate(false); }}>
              Cancelar
            </Button>
          </div>
        </Panel>
      )}

      {!accounts.length && (
        <Panel title="Sin cuentas configuradas">
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
            Añade el primer correo o acceso corporativo desde el botón superior.
          </p>
        </Panel>
      )}

      {accounts.map((acc) => (
        <Panel key={acc._id} title={acc.label || acc.type}>
          {editing === acc._id ? (
            <AccountForm form={form} onChange={setForm} />
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
              <ReadField label="Tipo" value={accountTypeLabels[acc.type] || acc.type || '—'} />
              <ReadField label="Email / usuario" value={acc.email || acc.username || '—'} />
              <ReadField label="Proveedor" value={acc.provider || '—'} />
              <ReadField label="Acceso" value={acc.loginUrl || '—'} link={acc.loginUrl} />
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '.85rem' }}>Estado</span>
                <Badge variant={acc.status === 'active' ? 'success' : acc.status === 'suspended' ? 'danger' : 'warning'}>
                  {accountStatusLabels[acc.status] || acc.status || '—'}
                </Badge>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.35rem' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '.85rem' }}>Entorno</span>
                <Badge variant={acc.environment === 'production' ? 'success' : 'warning'}>
                  {acc.environment || '—'}
                </Badge>
              </div>
            </div>
          )}
          <SecretField
            label="Contraseña / credencial"
            status={acc.credential?.hasSecret ? 'encrypted' : 'empty'}
            onReveal={() => onReveal(acc._id)}
            onRotate={() => {
              setRotatingId(rotatingId === acc._id ? null : acc._id);
              setPasswordForm('');
            }}
            lastViewed={formatDate(acc.credential?.lastViewedAt)}
            lastRotated={formatDate(acc.credential?.lastRotatedAt)}
          />
          {rotatingId === acc._id && (
            <div style={{ marginTop: '.75rem', display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) auto auto', gap: '.5rem', alignItems: 'end' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
                <span style={{ color: 'var(--text-secondary)' }}>Nueva contraseña guardada</span>
                <input
                  type="password"
                  value={passwordForm}
                  onChange={(e) => setPasswordForm(e.target.value)}
                  autoComplete="new-password"
                />
              </label>
              <Button
                iconLeft={<KeyIcon size={14} />}
                onClick={() => submitRotate(acc._id)}
                disabled={saving || !passwordForm.trim()}
              >
                Cambiar
              </Button>
              <Button variant="secondary" onClick={() => { setRotatingId(null); setPasswordForm(''); }}>
                Cancelar
              </Button>
            </div>
          )}
          <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
            {editing === acc._id ? (
              <>
                <Button onClick={() => { onSave(acc._id, form); setEditing(null); }} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
                <Button variant="secondary" onClick={() => setEditing(null)}>
                  Cancelar
                </Button>
              </>
            ) : (
              <Button variant="secondary" onClick={() => startEdit(acc)}>
                Editar
              </Button>
            )}
          </div>
        </Panel>
      ))}
    </div>
  );
}

function ReadField({ label, value, link }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '.25rem', minWidth: 0 }}>
      <span style={{ color: 'var(--text-secondary)', fontSize: '.85rem' }}>{label}</span>
      {link ? (
        <a href={link} target="_blank" rel="noreferrer" style={{ color: 'var(--a-cyan)', overflowWrap: 'anywhere' }}>
          {value}
        </a>
      ) : (
        <strong style={{ color: 'var(--text-primary)', overflowWrap: 'anywhere' }}>{value}</strong>
      )}
    </div>
  );
}

function AccountForm({ form, onChange, includeSecret = false }) {
  const setField = (key, value) => onChange((prev) => ({ ...prev, [key]: value }));
  const setSecret = (value) => onChange((prev) => ({
    ...prev,
    credential: { ...(prev.credential || {}), secretKind: 'password', plainSecret: value },
  }));

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Tipo</span>
        <select value={form.type || 'other'} onChange={(e) => setField('type', e.target.value)}>
          {Object.entries(accountTypeLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Etiqueta</span>
        <input value={form.label || ''} onChange={(e) => setField('label', e.target.value)} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Email</span>
        <input type="email" value={form.email || ''} onChange={(e) => setField('email', e.target.value)} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Usuario</span>
        <input value={form.username || ''} onChange={(e) => setField('username', e.target.value)} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Proveedor</span>
        <input value={form.provider || ''} onChange={(e) => setField('provider', e.target.value)} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
        <span style={{ color: 'var(--text-secondary)' }}>URL acceso</span>
        <input value={form.loginUrl || ''} onChange={(e) => setField('loginUrl', e.target.value)} />
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Estado</span>
        <select value={form.status || 'planned'} onChange={(e) => setField('status', e.target.value)}>
          {Object.entries(accountStatusLabels).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Propietario</span>
        <select value={form.owner || 'client'} onChange={(e) => setField('owner', e.target.value)}>
          <option value="client">Cliente</option>
          <option value="weltas">Weltas</option>
          <option value="shared">Compartido</option>
        </select>
      </label>
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Entorno</span>
        <select value={form.environment || 'production'} onChange={(e) => setField('environment', e.target.value)}>
          <option value="production">Producción</option>
          <option value="provisional">Provisional</option>
          <option value="internal">Interno</option>
        </select>
      </label>
      {includeSecret && (
        <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem' }}>
          <span style={{ color: 'var(--text-secondary)' }}>Contraseña inicial</span>
          <input
            type="password"
            value={form.credential?.plainSecret || ''}
            onChange={(e) => setSecret(e.target.value)}
            autoComplete="new-password"
          />
        </label>
      )}
      <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', fontSize: '0.85rem', gridColumn: '1 / -1' }}>
        <span style={{ color: 'var(--text-secondary)' }}>Uso / notas</span>
        <textarea
          rows={3}
          value={form.purpose || form.notes || ''}
          onChange={(e) => {
            setField('purpose', e.target.value);
            setField('notes', e.target.value);
          }}
        />
      </label>
    </div>
  );
}

function IntegracionesPanel({ integrations, loading, saving, onSave, onReveal }) {
  if (loading && !integrations.length) return <Skeleton width="100%" height="8rem" />;
  if (!integrations.length) return <p style={{ color: 'var(--text-secondary)' }}>No hay integraciones configuradas.</p>;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
      {integrations.map((intg) => (
        <IntegrationCard
          key={intg._id}
          name={intg.label || intg.provider}
          provider={intg.provider}
          status={intg.status === 'active' ? 'connected' : intg.status === 'error' ? 'error' : 'disconnected'}
          scopes={intg.scopes || []}
          syncStatus={intg.syncStatus}
          onConfigure={() => {}}
          onValidate={() => {}}
          onSync={() => {}}
        />
      ))}
    </div>
  );
}

function SyncPanel({ jobs, loading }) {
  if (loading && !jobs.length) return <Skeleton width="100%" height="6rem" />;
  if (!jobs.length) {
    return (
      <Panel title="Sincronización con portal.weltas.es">
        <p style={{ color: 'var(--text-secondary)' }}>
          No hay jobs de sincronización registrados. La infraestructura está preparada para cuando se active el sync.
        </p>
      </Panel>
    );
  }
  return (
    <Panel title="Sincronización con portal.weltas.es">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {jobs.map((job) => (
          <div key={job._id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <Badge variant={job.status === 'completed' ? 'success' : job.status === 'failed' ? 'danger' : 'warning'}>
              {job.status}
            </Badge>
            <span style={{ fontSize: '0.85rem' }}>{job.entityType} — {job.entityId}</span>
          </div>
        ))}
      </div>
    </Panel>
  );
}
